import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Embla-based product card carousel.
 * Replaces the scroll-based slideshow for product card galleries.
 * Exposes the same API surface that product-card.js expects from slideshow-component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides] - The slide elements (all, including hidden variant images)
 * @property {HTMLButtonElement} [previous] - The previous arrow button
 * @property {HTMLButtonElement} [next] - The next arrow button
 *
 * @extends {Component<Refs>}
 */
class CardGalleryCarousel extends Component {
  /** @type {any} */
  #embla = null;

  /** @type {number} */
  #current = 0;

  /** @type {HTMLElement | null} */
  #viewportEl = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initEmbla();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroyEmbla();
  }

  #destroyEmbla() {
    if (this.#embla) {
      this.#embla.destroy();
      this.#embla = null;
    }
  }

  #initEmbla() {
    this.#viewportEl = this.querySelector('.card-gallery-carousel__viewport');
    if (!this.#viewportEl) return;

    const EmblaCarousel = /** @type {any} */ (window).EmblaCarousel;
    if (!EmblaCarousel) return;

    this.#embla = EmblaCarousel(this.#viewportEl, {
      loop: true,
      duration: 20,
      dragFree: false,
      slides: 'slideshow-slide:not([hidden])',
    });

    this.#embla.on('select', () => {
      this.#onEmblaSelect();
    });

    // Set initial aria-hidden state
    this.#current = 0;
    this.#updateAriaHidden();
  }

  #onEmblaSelect() {
    const previousIndex = this.#current;
    const emblaIndex = this.#embla?.selectedSnap() ?? 0;

    // Map embla index back to visible slide index
    const slideNodes = this.#embla?.slideNodes() ?? [];
    const emblaSlide = slideNodes[emblaIndex];
    const visibleSlides = this.slides;
    const visibleIndex = visibleSlides?.indexOf(emblaSlide) ?? emblaIndex;

    this.#current = visibleIndex >= 0 ? visibleIndex : emblaIndex;
    this.#updateAriaHidden();

    const slide = visibleSlides?.[this.#current];
    if (slide) {
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: this.#current,
          previousIndex,
          userInitiated: true,
          trigger: 'select',
          slide,
          id: slide.getAttribute('slide-id'),
        })
      );
    }
  }

  #updateAriaHidden() {
    const visibleSlides = this.slides;
    visibleSlides?.forEach((s, i) => {
      s.setAttribute('aria-hidden', String(i !== this.#current));
    });
  }

  /**
   * Find the embla slide index for a given slide element.
   * @param {HTMLElement} slide
   * @returns {number}
   */
  #getEmblaIndex(slide) {
    const slideNodes = this.#embla?.slideNodes() ?? [];
    return slideNodes.indexOf(slide);
  }

  // -- Public API (compatible with slideshow-component for product-card.js) --

  /** Whether this carousel is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  /** Returns only visible (non-hidden) slides. */
  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden'));
  }

  /** Current visible slide index. */
  get current() {
    return this.#current;
  }

  set current(value) {
    this.#current = value;
  }

  /** The first slide element. */
  get initialSlide() {
    return this.refs.slides?.[0];
  }

  /** The initial slide index (always 0 for product cards). */
  get initialSlideIndex() {
    return 0;
  }

  /**
   * Selects a slide by index or ID.
   * @param {number | string | {id: string}} input
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  select(input, event, options = {}) {
    if (typeof input === 'object' && input !== null && 'id' in input) {
      const allSlides = this.refs.slides || [];
      const target = allSlides.find((s) => s.getAttribute('slide-id') == input.id);
      if (!target) return;

      if (target.hasAttribute('hidden')) {
        target.removeAttribute('hidden');
      }

      if (this.#embla) {
        this.#embla.reInit();
        const emblaIndex = this.#getEmblaIndex(target);
        if (emblaIndex >= 0) {
          const instant = options.animate === false;
          this.#embla.goTo(emblaIndex, instant);
        }
      }

      const visibleSlides = this.slides;
      this.#current = visibleSlides?.indexOf(target) ?? 0;
      this.#updateAriaHidden();
    } else {
      const index = typeof input === 'number' ? input : parseInt(String(input), 10);
      if (isNaN(index)) return;

      const visibleSlides = this.slides;
      if (!visibleSlides?.length) return;

      const wrappedIndex = ((index % visibleSlides.length) + visibleSlides.length) % visibleSlides.length;
      const target = visibleSlides[wrappedIndex];
      if (!target) return;

      if (this.#embla) {
        const emblaIndex = this.#getEmblaIndex(target);
        if (emblaIndex >= 0) {
          const instant = options.animate === false;
          this.#embla.goTo(emblaIndex, instant);
        }
      }

      this.#current = wrappedIndex;
      this.#updateAriaHidden();
    }
  }

  /**
   * Advance to the next slide.
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  next(event, options = {}) {
    event?.preventDefault();
    const visibleSlides = this.slides;
    if (!visibleSlides?.length) return;

    if (this.#embla) {
      this.#embla.goToNext(options.animate === false);
    } else {
      this.#current = (this.#current + 1) % visibleSlides.length;
      this.#updateAriaHidden();
    }
  }

  /**
   * Go back to the previous slide.
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  previous(event, options = {}) {
    event?.preventDefault();
    const visibleSlides = this.slides;
    if (!visibleSlides?.length) return;

    if (this.#embla) {
      this.#embla.goToPrev(options.animate === false);
    } else {
      this.#current = (this.#current - 1 + visibleSlides.length) % visibleSlides.length;
      this.#updateAriaHidden();
    }
  }
}

if (!customElements.get('card-gallery-carousel')) {
  customElements.define('card-gallery-carousel', CardGalleryCarousel);
}
